import { useCallback } from "react";
import classnames from "classnames";
import dynamic from "next/dynamic";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import pluralize from "pluralize";

import { DISPENSARY_NEW_FILTER_STRINGS } from "@/constants/dispensary";

const Dropdown = dynamic(() => import("@/components/botanic/Dropdown"), {
  ssr: true,
});

import usePublishFilterClickEvent from "../../MenuFilters/hooks/usePublishFilterClickEvent";
import useSortDropDown from "../hooks/useSortDropDown";

const DispensarySortDropDown: React.FC<{
  availableSorts: string[];
  className?: string;
  totalItems: number;
}> = ({ availableSorts, className, totalItems }) => {
  const { push } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { selectMenuSorts } = useSortDropDown(availableSorts);
  const { publishSortDropDownClick } = usePublishFilterClickEvent();
  const sortValue = searchParams?.get("sort");
  const searchParamsString = searchParams?.toString();

  const handleClick = useCallback(
    (value: string) => {
      const targetSortSearchParams = new URLSearchParams(searchParamsString);
      targetSortSearchParams.delete("page");
      targetSortSearchParams.delete("boost_strain");
      targetSortSearchParams.set(selectMenuSorts.name, value);
      publishSortDropDownClick(value);

      push(`${pathname}?${targetSortSearchParams.toString()}`);
    },
    [publishSortDropDownClick, searchParamsString],
  );

  return (
    <div
      className={classnames("flex justify-between items-start", className)}
      data-testid="filter-drop-down"
    >
      <div className="flex items-start">
        {totalItems > 0 && (
          <div className="flex-shrink-0 font-bold mr-xxl">
            {pluralize("Product", totalItems, true)}
          </div>
        )}
      </div>

      <div
        data-testid="sort-dropdown"
        className="flex flex-shrink-0 items-center justify-center ml-lg"
      >
        <Dropdown
          label="Sort by"
          variant="small"
          preselectedOption={sortValue || "recommended"}
          options={[
            "recommended",
            ...selectMenuSorts.values.map(({ value }) => value),
          ]}
          onItemRender={(option) => ({
            children: DISPENSARY_NEW_FILTER_STRINGS[option] || "Recommended",
            onClick: () => handleClick(option),
          })}
        />
      </div>
    </div>
  );
};

export default DispensarySortDropDown;
